import { ISituacaoAcesso, SituacaoAssinatura } from "../interfaces/ISituacaoAcesso";

type Cor = "success" | "error" | "default";

export interface IApresentacaoSituacao {
    /** Rótulo do chip. Binário de propósito: o card não tem espaço para mais. */
    rotulo: "Ativa" | "Inativa" | "—";
    cor: Cor;
    /** Cor concreta, para bordas e fundos derivados. */
    hex: string;
    /**
     * O detalhamento do rótulo, para o hover: as causas do bloqueio quando
     * inativa, notas de contexto quando ativa. Vazio quando não há nada a dizer.
     */
    motivos: string[];
}

const CORES: Record<Cor, string> = {
    success: "#2e7d32",
    error: "#c62828",
    default: "#9e9e9e",
};

export const rotuloAssinatura: Record<SituacaoAssinatura, string> = {
    ATIVA: "Assinatura ativa",
    CANCELADA: "Assinatura cancelada",
    SEM_ASSINATURA: "Sem assinatura",
};

const formatarData = (data?: string | null) =>
    data ? new Date(data).toLocaleDateString("pt-BR") : null;

/**
 * Como a situação aparece na tela.
 *
 * Quem decide se está bloqueado é o ms-estabelecimentos (`situacao.bloqueado`),
 * não esta função — ela apenas enumera as causas. As três podem valer ao mesmo
 * tempo, então o hover lista todas em vez de mostrar só a de maior precedência.
 */
export const apresentarSituacao = (situacao?: ISituacaoAcesso): IApresentacaoSituacao => {
    if (!situacao) {
        return { rotulo: "—", cor: "default", hex: CORES.default, motivos: [] };
    }

    if (situacao.bloqueado) {
        const motivos: string[] = [];

        if (situacao.inativado) {
            motivos.push("Estabelecimento inativado.");
        }

        if (situacao.bloqueadoAdministrativamente) {
            const desde = formatarData(situacao.bloqueadoAs);
            const motivo = situacao.motivoBloqueio || "motivo não informado";
            motivos.push(desde ? `Bloqueado em ${desde}: ${motivo}` : `Bloqueado: ${motivo}`);
        }

        if (situacao.situacaoAssinatura === "CANCELADA") {
            const em = formatarData(situacao.dataFimAssinatura);
            motivos.push(em ? `Assinatura cancelada em ${em}.` : "Assinatura cancelada.");
        }

        return {
            rotulo: "Inativa",
            cor: "error",
            hex: CORES.error,
            // Fallback: o ms bloqueou por algo que ainda não sabemos nomear.
            motivos: motivos.length ? motivos : [situacao.motivo ?? "Acesso bloqueado."],
        };
    }

    // Ativa: nada aqui impede de entrar, é só contexto.
    const notas: string[] = [];

    if (situacao.situacaoAssinatura === "SEM_ASSINATURA") {
        notas.push("Nenhuma assinatura cadastrada.");
    }

    if (situacao.freeTier) {
        const ate = formatarData(situacao.fimPeriodoTeste);
        notas.push(ate ? `Período de teste até ${ate}.` : "Em período de teste.");
    }

    if (situacao.planoNome) {
        notas.push(`Plano ${situacao.planoNome}.`);
    }

    return { rotulo: "Ativa", cor: "success", hex: CORES.success, motivos: notas };
};
